import { useEffect, useRef, useState } from 'react';

export interface UseFormFieldValidationConfig {
  errorMessage: string;
  disablesForm?: boolean;
  test: (value: string) => boolean;
}

export interface UseFormFieldConfig {
  name: string;
  startValue: string;
  validations: Record<string, UseFormFieldValidationConfig>;
}

export interface UseFormConfig {
  validateOnSubmit: boolean;
  submitted: boolean;
  fields: UseFormFieldConfig[];
}

export type UseFormErrorData = Record<string, Pick<UseFormFieldValidationConfig, 'errorMessage' | 'disablesForm'>>;

export interface UseFormErrors {
  errors?: Record<string, UseFormErrorData>;
  enabled?: boolean;
}

export interface UseFormState {
  valid: boolean;
  enabled: boolean;
  errors?: Record<string, UseFormErrorData>;
  values: Record<string, string>;
}

export interface UseFormReturnType extends UseFormState {
  reset: () => void;
  setValue: (fieldName: string, value: string) => void;
  setValues: (values: Record<string, string>) => void;
}

export const basicRequiredValidation: UseFormFieldValidationConfig = {
  errorMessage: '',
  test: (value: string) => Boolean(value),
  disablesForm: true,
};

const useForm = ({ fields, submitted, validateOnSubmit }: UseFormConfig): UseFormReturnType => {
  const setValuesFromFields = (allFields: UseFormFieldConfig[]): Record<string, string> => {
    return allFields.reduce<Record<string, string>>((acc, field) => {
      acc[field.name] = field.startValue;
      return acc;
    }, {});
  };

  const [formState, setFormState] = useState<UseFormState>({
    valid: false,
    enabled: false,
    errors: {},
    values: setValuesFromFields(fields),
  });
  const initialFields = useRef(fields);

  const updateState = (subState: Partial<typeof formState>): void => {
    setFormState(formState => {
      return {
        ...formState,
        ...subState,
      };
    });
  };

  const getErrors = (): UseFormErrors => {
    const { values } = formState;
    return fields.reduce<UseFormErrors>(
      (acc, field) => {
        const { validations, name: fieldName } = field;

        Object.keys(validations).forEach(key => {
          const validation = validations[key];

          if (typeof validation.test === 'undefined') {
            throw new Error('you must define a .test() method on validations');
          }

          if (!validation.test(values[fieldName])) {
            acc.enabled = acc.enabled && !validation.disablesForm;

            if (acc.errors === undefined) {
              acc.errors = {};
            }

            acc.errors[fieldName] = {
              ...acc.errors[fieldName],
              [key]: (({ disablesForm, errorMessage }) => ({
                disablesForm,
                errorMessage,
              }))(validation),
            };
          }
        });

        return acc;
      },
      {
        errors: {},
        enabled: true,
      },
    );
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (submitted && validateOnSubmit) {
      const currentErrors = getErrors();
      updateState({
        errors: currentErrors.errors,
        valid: Object.keys(currentErrors.errors ?? {}).length === 0,
      });
    }
  }, [submitted]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const currentErrors = getErrors();

    updateState({
      ...(!validateOnSubmit && { errors: currentErrors.errors }),
      valid: Object.keys(currentErrors.errors ?? {}).length === 0,
      enabled: currentErrors.enabled,
    });
  }, Object.values(formState.values));

  const reset = (): void => {
    updateState({
      values: setValuesFromFields(initialFields.current),
      errors: {},
    });
  };

  const setValue = (fieldName: string, value: string): void => {
    updateState({
      values: { ...formState.values, [fieldName]: value },
    });
  };

  const setValues = (values: Record<string, string>): void => {
    updateState({
      values: { ...formState.values, ...values },
    });
  };

  return {
    reset,
    setValue,
    setValues,
    ...formState,
  };
};

export default useForm;
